import cx from 'classnames'
import { Link } from 'react-router-dom'
import { ROUTE_PATH } from 'lib/paths'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { logEvent } from 'lib/analytics'
import { Group, Post, User } from 'lib/_types'
import { Gift, GiftSource, GiftTargetObject } from 'lib/_types/gift'
import {
  useGetGiftsListQuery,
  useSendGiftCallMutation,
  useSendGiftGroupMutation,
  useSendGiftPostMutation,
  useSendGiftUserMutation,
} from 'store/api/yay/gifts'
import { useCurrentUser } from 'store/api/yay/user'
import { CallUser } from 'store/slices/agora'
import { useResponsive } from 'hooks'

import { Switch } from '..'
import Skeleton from 'react-loading-skeleton'
import Avatar from 'components/Avatar'
import Button from 'components/Button'
import TokenDisplay from 'components/Token/TokenDisplay'
import { GroupName } from 'components/GroupList/components/GroupName'
import { GroupCategoryLabel } from 'components/GroupList/components/GroupCategoryLabel'
import SendGiftUserSelect from './SendGiftUserSelect/SendGiftUserSelect'

import './style.scss'

const SendGift = ({
  giftType,
  giftTarget,
  recipients,
  onSend,
  budget,
}: {
  giftType: GiftSource
  giftTarget: GiftTargetObject
  recipients: Array<Partial<User> | CallUser>
  onSend: Function
  budget: number
}) => {
  const { t } = useTranslation()
  const mountedRef = useRef(true)
  const { isMobile } = useResponsive()

  const giftSourceDesc = useMemo(() => {
    switch (giftType) {
      case 'post':
        return t('common.post')
      case 'circle_post':
        const group = (giftTarget as Post).group
        const topic = group?.topic
        return (
          <Fragment>
            <div>
              <Link to={ROUTE_PATH.GROUP(group?.id)} className="GroupName">
                {t('gift.post_from_group', { topic })}
              </Link>
            </div>
            <GroupCategoryLabel group={group} />
          </Fragment>
        )
      case 'circle':
        return (
          <Fragment>
            <div>
              <GroupName group={giftTarget as Group} />
            </div>
            <div>
              <GroupCategoryLabel group={giftTarget as Group} />
            </div>
          </Fragment>
        )
      case 'user_profile':
        return t('common.profile')
      case 'call':
        return t('gift.conference_call')
    }
  }, [giftType, recipients])
  const [selectedGift, setSelectedGift] = useState<Gift>()
  const [selectedRecipient, setSelectedRecipient] = useState(null)
  const [activeRecipient, setActiveRecipient] = useState(null)
  const [isAnonymous, setIsAnonymous] = useState(false)
  const giftsListRes = useGetGiftsListQuery({ currency: 'empl' })

  const [sendGiftUser] = useSendGiftUserMutation()
  const [sendGiftPost] = useSendGiftPostMutation()
  const [sendGiftGroup] = useSendGiftGroupMutation()
  const [sendGiftCall] = useSendGiftCallMutation()

  const { data: currentUser } = useCurrentUser()

  const NUM_GIFTS = 1 // can only send 1 gift at a time for now

  useEffect(() => {
    if (giftType !== 'call') {
      setSelectedRecipient(recipients[0])
    }
  }, [recipients])

  const handleSendGiftAction = async () => {
    const giftObj = { [selectedGift.id?.toString()]: 1 }
    let res

    switch (giftType) {
      case 'user_profile':
        res = await sendGiftUser({
          userId: Number(selectedRecipient.id),
          gifts: giftObj,
          anonymous: isAnonymous,
        })
        break
      case 'post':
      case 'circle_post':
        res = await sendGiftPost({
          postId: giftTarget.id,
          gifts: giftObj,
          anonymous: isAnonymous,
        })
        break
      case 'circle':
        res = await sendGiftGroup({
          groupId: giftTarget.id,
          recipientId: Number(selectedRecipient.id),
          gifts: giftObj,
          anonymous: isAnonymous,
        })
        break
      case 'call':
        res = await sendGiftCall({
          callId: giftTarget.id,
          recipientId: Number(selectedRecipient.id),
          gifts: giftObj,
        })
        break
    }

    if (mountedRef.current && res && res?.['data']?.result == 'success') {
      onSend({
        sentGift: { gift: selectedGift, num: NUM_GIFTS },
        recipient: selectedRecipient,
      })
    } else if (mountedRef.current && res && res?.['data']?.result == 'error') {
      logEvent.gift_error({
        type: 'gift_send',
        error_code: res?.['data']?.error_code,
      })
    }
  }

  return (
    <div className="SendGiftPanel">
      <div className="RecipientHeader">
        <div
          className={cx('UserInfo__wrapper', {
            'UserInfo__wrapper--selectable':
              giftType === 'call' && selectedRecipient,
          })}
        >
          {selectedRecipient && (
            <Fragment>
              {giftType === 'call' && (
                <div
                  className="UserInfo__wrapper--selectable__handler"
                  onClick={() => setSelectedRecipient(null)}
                ></div>
              )}
              <div className="UserInfo__icon">
                <Avatar user={selectedRecipient} />
              </div>
              <div
                className={cx('UserInfo__name', {
                  'UserInfo__name--selectable': giftType === 'call',
                })}
              >
                {selectedRecipient.nickname}
              </div>
            </Fragment>
          )}
        </div>
        {giftSourceDesc && (
          <div className="RecipientHeader__description">{giftSourceDesc}</div>
        )}
      </div>
      {giftsListRes?.isFetching && (
        <>
          <div className="GiftSelectionPanel__wrapper--no-grid">
            <Skeleton height={(isMobile ? 700 : 512) - 32} width="100%" />
          </div>
          <div className="GiftSelectionPanel__button">
            <Skeleton height={63} width="100%" />
          </div>
        </>
      )}
      {giftsListRes?.isSuccess && (
        <>
          {giftType === 'call' && !selectedRecipient && (
            <div className="GiftRecipientSelectionPanel__wrapper">
              <SendGiftUserSelect
                userList={recipients
                  .filter((user) => user.id !== currentUser.id)
                  .map((user) => user as CallUser)}
                activeRecipient={activeRecipient}
                onSelectActiveRecipient={(user) => setActiveRecipient(user)}
              />
            </div>
          )}
          {(giftType !== 'call' || selectedRecipient) && (
            <div className="GiftSelectionPanel__wrapper">
              {giftsListRes?.data?.gifts?.map((gift: Gift) => {
                return (
                  <div
                    key={gift.id}
                    className={cx('GiftSelectionPanel__item', {
                      'GiftSelectionPanel__item--active':
                        selectedGift && gift.id === selectedGift?.id,
                      'GiftSelectionPanel__item--disabled': gift.price > budget,
                    })}
                    onClick={() => setSelectedGift(gift)}
                  >
                    <div className="GiftSelectionPanel__item__image">
                      <img src={gift.icon_thumbnail} alt={gift.title} />
                    </div>
                    <div className="GiftSelectionPanel__item__price">
                      <TokenDisplay type={gift.currency} displaySize="compact">
                        {gift.price?.toLocaleString()}
                      </TokenDisplay>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {(giftType !== 'call' || selectedRecipient) && (
            <Fragment>
              {giftType !== 'call' && (
                <div className="GiftSelectionPanel__option">
                  <div>{t('gift.send_anonymously')}</div>
                  <div>
                    <Switch
                      checked={isAnonymous}
                      onChange={(c) => setIsAnonymous(c)}
                    />
                  </div>
                </div>
              )}
              <div className="GiftSelectionPanel__button">
                <Button
                  buttonStyle="secondary"
                  disabled={!selectedGift}
                  onClick={handleSendGiftAction}
                >
                  {t('common.generic_send')}
                </Button>
              </div>
            </Fragment>
          )}
          {giftType === 'call' && !selectedRecipient && (
            <Fragment>
              <div className="GiftSelectionPanel__button">
                <Button
                  buttonStyle="secondary"
                  onClick={() => setSelectedRecipient(activeRecipient)}
                  disabled={!activeRecipient}
                >
                  {t('common.authenticate')}
                </Button>
              </div>
            </Fragment>
          )}
        </>
      )}
    </div>
  )
}

export default SendGift
